import type { ReactNode } from "react";
import { UserManager } from "oidc-client-ts";
import { createOidcSettings } from "./oidcSettings";
import { SessionProvider, useAuthContext, type AuthContextValue } from "./SessionProvider";
import { SessionGuard } from "./SessionGuard";
import { SignInCallback } from "./SignInCallback";
import {
  isRedirecting,
  readReturnPath,
  signInAgain as signInAgainWith,
  startSignIn as startSignInWith,
  startSignOut as startSignOutWith,
  type SignInOptions,
} from "./identityRedirect";

export interface CreateAuthOptions {
  /** The app's OpenIddict client id, as its `Identity:Apps` registry block names it. */
  clientId: string;
  /** The identity server's origin. Read when needed: config loads after the module. */
  getIdentityUrl: () => string;
  /** False in a local run without an identity server: no user manager is made and nothing needs a sign-in. */
  isEnabled: () => boolean;
  /**
   * The app's own user manager, when it keeps one (one per tab; see createOidcSettings). Without it, createAuth makes one
   * from the client id and identity server, on first use. Made only when sign-in is on.
   */
  getUserManager?: () => UserManager;
  /** The scopes the app asks for, when createAuth makes the user manager. Default: createOidcSettings'. */
  scope?: string;
  /** What every sign-in from this app carries, read when the sign-in starts (the employer app's `sf_env`). */
  getSignInParams?: () => Record<string, string>;
  /** Where the sign-in callback goes when no page was remembered. */
  homePath: string;
  /** What the app shows while a session loads, the browser leaves to sign in, or a callback is exchanged. */
  busyView: ReactNode;
  /** What the app shows when a sign-in callback fails, given the failure's message. */
  errorView: (message: string) => ReactNode;
}

export interface Auth {
  AuthProvider: (props: { children: ReactNode }) => ReactNode;
  useAuth: () => AuthContextValue;
  /** A route that needs a signed-in person. */
  AuthGuard: (props: { children: ReactNode }) => ReactNode;
  /** The `/auth/callback` route. */
  CallbackPage: () => ReactNode;
  /** Sends the browser to the Optimyl sign-in, once, with the app's parameters. */
  startSignIn: (options?: SignInOptions) => Promise<void>;
  /** The session is gone: sign in again and come back here. Failures to start are reported under `context`. */
  signInAgain: (context: string) => void;
  /** Signs out of every Optimyl app in this browser; `before` runs once the tab is marked as leaving. */
  startSignOut: (before?: () => Promise<void>) => Promise<void>;
  /** Whether the tab is leaving for a sign-in or a sign-out: nothing it shows will be seen. */
  isRedirecting: () => boolean;
  /** The page remembered for after sign-in, or null. */
  readReturnPath: () => string | null;
  /** This tab's user manager (for an access token outside React). Only while sign-in is on. */
  getUserManager: () => UserManager;
}

/**
 * One app's sign-in and sign-out, bound once: its client, its identity server, its sign-in parameters and its views.
 * Everything here is the same in every app; nothing the app passes changes how it works.
 */
export function createAuth(options: CreateAuthOptions): Auth {
  const getSignInParams = options.getSignInParams ?? (() => ({}));
  let ownUserManager: UserManager | null = null;
  const getUserManager = options.getUserManager ?? (() =>
    (ownUserManager ??= new UserManager(createOidcSettings({ identityUrl: options.getIdentityUrl(), clientId: options.clientId, scope: options.scope }))));
  const userManagerIfEnabled = () => (options.isEnabled() ? getUserManager() : null);

  function AuthProvider({ children }: { children: ReactNode }) {
    return (
      <SessionProvider userManager={userManagerIfEnabled()} identityUrl={options.getIdentityUrl()} clientId={options.clientId} getSignInParams={getSignInParams}>
        {children}
      </SessionProvider>
    );
  }

  function AuthGuard({ children }: { children: ReactNode }) {
    return <SessionGuard busyView={options.busyView} errorView={options.errorView}>{children}</SessionGuard>;
  }

  function CallbackPage() {
    return <SignInCallback getUserManager={getUserManager} homePath={options.homePath} busyView={options.busyView} errorView={options.errorView} />;
  }

  return {
    AuthProvider,
    useAuth: useAuthContext,
    AuthGuard,
    CallbackPage,
    startSignIn: (signInOptions) => startSignInWith(getUserManager(), signInOptions, getSignInParams()),
    signInAgain: (context) => signInAgainWith(getUserManager(), context, getSignInParams()),
    startSignOut: (before) => startSignOutWith(userManagerIfEnabled(), options.getIdentityUrl(), options.clientId, before),
    isRedirecting,
    readReturnPath,
    getUserManager,
  };
}
